import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { contentApi, ContentType } from '@/services/contentApi';

interface NextActionsPanelProps {
  contentType: ContentType;
  contentId: string;
}

/**
 * Drop-in "what can I do next with this" rail, same convention as
 * AssignButton/ReviewPanel — renders nothing if this content type has no
 * configured next actions (most won't; see contentRegistry.ts's
 * nextActions field). Each action is either a clickable button or a
 * disabled one naming the specific blocker (permission/plan/review),
 * computed live by the backend rather than just linking blindly.
 */
const NextActionsPanel: React.FC<NextActionsPanelProps> = ({ contentType, contentId }) => {
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['content-next-actions', contentType, contentId],
    queryFn: () => contentApi.getNextActions(contentType, contentId),
  });

  if (!data || data.length === 0) return null;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Next steps</h3>
      <div className="flex flex-wrap gap-2">
        {data.map((action) => (
          <button
            key={action.label}
            disabled={!action.eligible}
            title={action.eligible ? undefined : action.reason}
            onClick={() => action.eligible && navigate(action.route)}
            className={`inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border transition-colors ${
              action.eligible
                ? 'border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            {action.label}
            <ArrowRightIcon className="h-3.5 w-3.5" />
          </button>
        ))}
      </div>
      {data.some((a) => !a.eligible) && (
        <p className="mt-2 text-xs text-gray-400">
          {data.find((a) => !a.eligible)?.reason}
        </p>
      )}
    </div>
  );
};

export default NextActionsPanel;
